import { z } from "zod";
import { isFormFieldKey } from "./formNames";
import { formInputsOf, isFormBlock, type FormBlock, type FormField, type FormFields, type FormTextsResolved } from "./forms";

/** The form builder, round two (brief docs/briefs/forms-round-two.md with "After the red team's review"; red team report findings 6 to 12):
 *  the pure rules a submission follows, so the open routes, the form's own page and the embed read one truth.
 *
 *  In plain words: a **submission** is what a visitor sends: the values by field key, optionally the project the snippet names (the
 *  **placement**), and **marks** saying which values the page filled in for the person (from the page's link, the container's attributes,
 *  the shop's own call, or the plugin's server). A typed value carries no mark. Only the plugin's server, sending with its site key, may mark
 *  a value "plugin" (verified); the API checks that, not this module. */

/** where a prefilled value came from: the page's address (`?<key>=`), the container's `data-prefill-<key>`, the host page's
 *  `OmnaBaseForms.prefill()` call, the WordPress plugin's server (only with the site's key) */
export const FORM_PREFILL_SOURCES = ["url", "attribute", "host", "plugin"] as const;
export type FormPrefillSource = (typeof FORM_PREFILL_SOURCES)[number];

/** the limits of one submission: 40 values (a form's field limit), a value of 20,000 characters (a long text), a short text of 500; files:
 *  one per file field, five together, 10 MB each and 25 MB in all (the mail's limits, the red team's question 7) */
export const FORM_SUBMISSION_BOUNDS = Object.freeze({ values: 40, valueChars: 20_000, shortChars: 500, files: 5, fileBytes: 10 * 1024 * 1024, totalBytes: 25 * 1024 * 1024 });

const FieldKey = z.string().refine(isFormFieldKey, "A field key: lower-case letters, digits and underscores");
const record = <T extends z.ZodType>(value: T) => z.record(FieldKey, value).refine((r) => Object.keys(r).length <= FORM_SUBMISSION_BOUNDS.values, `At most ${FORM_SUBMISSION_BOUNDS.values} values`);
/** a submission's body (strict: an unknown part is refused); `trap` is the embed's hidden field that people never see and scripts fill */
export const FormSubmissionSchema = z.object({
  values: record(z.union([z.string().max(FORM_SUBMISSION_BOUNDS.valueChars), z.boolean()])),
  project: z.string().min(1).max(100).optional(),
  prefill: record(z.enum(FORM_PREFILL_SOURCES)).optional(),
  turnstile: z.string().max(4096).optional(),
  trap: z.string().max(500).optional(),
  /** the plugin's forms round: the site's prefill token from the container (`data-prefill-token`); the server decides what it verifies */
  prefillToken: z.string().max(8192).optional(),
}).strict();
export type FormSubmission = z.infer<typeof FormSubmissionSchema>;
export type FormValues = Record<string, string | boolean>;

/** an open project a submission may land in, or a project field may still offer */
export type FormProjectRef = { id: string; slug: string; name: string };
/** an item as the open definition read shows it: a field never with a fixed hidden value, a Text block as exactly its key, words and element */
export type PublicFormField = Omit<FormField, "hiddenValue"> | FormBlock;
/** the open definition read (`GET /api/public/v1/forms/:workspace/:slug`): what a page or an embed needs to draw the form, nothing else */
export type PublicFormDefinition = { /** the form embed round: the form's id, the name the snippets use */ id: string; name: string; slug: string;
  /** the five words, resolved (never empty) */ texts: FormTextsResolved;
  /** the form embed round: the shield's name and tooltip on a value the company's site vouched for */ verified: FormVerifiedWords;
  fields: PublicFormField[]; ready: boolean; turnstileSiteKey: string | null };
/** the form embed round (question 12): the words of the shield the embed shows on a value the site's prefill token filled in; fixed words
 *  from the server (not editable), so the embed carries none of its own */
export type FormVerifiedWords = { label: string; tip: string };
export const FORM_VERIFIED_WORDS: Readonly<FormVerifiedWords> = Object.freeze({ label: "Verified", tip: "Filled in from your account on this site." });
/** the plugin's list of forms (`GET /api/public/v1/forms`, its site key); the plugin's forms round: `defaultFor`, the slugs of the key's own
 *  projects whose default form this is, so a shortcode without a form named shows the project's default form */
export const PublicFormListSchema = z.object({ data: z.array(z.object({ slug: z.string(), name: z.string(), defaultFor: z.array(z.string()) })) });
export type PublicFormList = z.infer<typeof PublicFormListSchema>;

const isProjectField = (f: Pick<FormField, "mapping">) => f.mapping === "project";
/** a hidden field whose value the company fixed (its value is the server's to add, never the visitor's) */
const isFixedHidden = (f: FormField) => f.type === "hidden" && !!f.hiddenValue;
const liveOptions = (f: FormField, open: readonly FormProjectRef[]) => (f.options ?? []).filter((o) => open.some((p) => p.id === o.value));
const projectFieldOf = (fields: FormFields) => formInputsOf(fields.items).find(isProjectField);

/** the items a visitor sees: no fixed hidden field, no hidden value anywhere, a project field's list without projects that closed since; a
 *  Text block as exactly its key, words and element (a paragraph when a stored block lacks one) */
export function publicFormFields(fields: FormFields, openProjects: readonly FormProjectRef[]): PublicFormField[] {
  return fields.items.filter((f) => isFormBlock(f) || !isFixedHidden(f)).map((f): PublicFormField => {
    if (isFormBlock(f)) return { key: f.key, type: "block", text: f.text, as: f.as ?? "p" };
    const { hiddenValue: _hidden, ...rest } = f;
    return isProjectField(f) ? { ...rest, options: liveOptions(f, openProjects) } : rest;
  });
}

/** whether a form can place a submission by itself: a project field with a live choice, or a default project that is still open */
export function formIsReady(f: { fields: FormFields; defaultProjectId: string | null }, openProjects: readonly FormProjectRef[]): boolean {
  const field = projectFieldOf(f.fields);
  if (field && liveOptions(field, openProjects).length > 0) return true;
  return !!f.defaultProjectId && openProjects.some((p) => p.id === f.defaultProjectId);
}

export type FormValuesChecked = { ok: true; values: FormValues } | { ok: false; errors: Record<string, string> };
const ADDRESS = z.email();

/** one field's value, checked and tidied: the value to keep (undefined: nothing to keep) or the sentence that names the fault */
function checkOne(f: FormField, raw: string | boolean | undefined, open: readonly FormProjectRef[]): { value?: string | boolean } | { error: string } {
  if (isFixedHidden(f)) return { value: f.hiddenValue };
  if (f.type === "checkbox") {
    if (raw !== undefined && typeof raw !== "boolean") return { error: "Tick the box or leave it empty." };
    if (f.required && raw !== true) return { error: "Tick this box to continue." };
    return { value: raw };
  }
  if (typeof raw === "boolean") return { error: "This field takes text." };
  const text = (raw ?? "").trim();
  if (!text) return f.required ? { error: "Fill in this field." } : {};
  if (f.type === "text" && text.length > FORM_SUBMISSION_BOUNDS.shortChars) return { error: `At most ${FORM_SUBMISSION_BOUNDS.shortChars} characters.` };
  if (f.type === "hidden" && text.length > FORM_SUBMISSION_BOUNDS.shortChars) return { error: "This value is too long." };
  if (f.type === "email" && !ADDRESS.safeParse(text).success) return { error: "Enter a valid email address." };
  if (f.type === "select") {
    const offered = isProjectField(f) ? liveOptions(f, open) : f.options ?? [];
    if (!offered.some((o) => o.value === text)) return { error: "Pick one of the options." };
  }
  return { value: text };
}

/** every value against the form: trimmed, an empty optional value dropped, a fixed hidden value forced; or every field at fault at once, one
 *  sentence each, under the field's key (a key the form lacks, a value sent for a file field, and one under a Text block's key are faults
 *  too; files travel apart, a block is never sent) */
export function checkFormValues(fields: FormFields, values: Record<string, unknown>, openProjects: readonly FormProjectRef[]): FormValuesChecked {
  const errors: Record<string, string> = {}; const out: FormValues = {};
  const byKey = new Map(fields.items.map((f) => [f.key, f]));
  for (const key of Object.keys(values)) {
    const f = byKey.get(key);
    if (!f) errors[key] = "This form has no such field.";
    else if (isFormBlock(f)) errors[key] = "A text block takes no value.";
    else if (f.type === "file") errors[key] = "Attach the file instead.";
  }
  for (const f of formInputsOf(fields.items)) {
    if (f.type === "file" || errors[f.key]) continue;
    const raw = values[f.key];
    const r = checkOne(f, raw as string | boolean | undefined, openProjects);
    if ("error" in r) errors[f.key] = r.error;
    else if (r.value !== undefined) out[f.key] = r.value;
  }
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, values: out };
}

export type FormPlace = { projectId: string } | { problem: "not_ready" | "unknown_place" };
type PlaceInput = { fields: FormFields; values: Record<string, unknown>; defaultProjectId: string | null; placement: string | null; openProjects: readonly FormProjectRef[] };
/** where a submission lands: the person's pick in the project field, else the placement (the snippet's or the plugin's project slug), else
 *  the form's default while it is open; a placement that names no open project is an error, never quietly the default */
export function formProjectOf(o: PlaceInput): FormPlace {
  const place = formPlaceOf(o);
  return "problem" in place ? place : { projectId: place.projectId };
}
/** the same, saying how the project was chosen (round three, finding 20: stored as the submission's `placed_by`) */
export function formPlaceOf(o: PlaceInput): { projectId: string; by: "field" | "placement" | "default" } | { problem: "not_ready" | "unknown_place" } {
  const field = projectFieldOf(o.fields);
  const pick = field ? o.values[field.key] : undefined;
  if (typeof pick === "string" && o.openProjects.some((p) => p.id === pick)) return { projectId: pick, by: "field" };
  if (o.placement) {
    const placed = o.openProjects.find((p) => p.slug === o.placement!.toLowerCase());
    return placed ? { projectId: placed.id, by: "placement" } : { problem: "unknown_place" };
  }
  if (o.defaultProjectId && o.openProjects.some((p) => p.id === o.defaultProjectId)) return { projectId: o.defaultProjectId, by: "default" };
  return { problem: "not_ready" };
}

const MARK_WORDS: Record<FormPrefillSource, string> = { url: " (unverified)", attribute: " (unverified)", host: " (unverified)", plugin: " (verified)" };
/** a value as the team reads it: a select's option label, the project field's project name, a checkbox Yes or No */
function shownValue(f: FormField, value: string | boolean, open: readonly FormProjectRef[]): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isProjectField(f)) return open.find((p) => p.id === value)?.name ?? f.options?.find((o) => o.value === value)?.label ?? value;
  if (f.type === "select") return f.options?.find((o) => o.value === value)?.label ?? value;
  return value;
}

/** the ticket's first message: one "Label: value" line per filled field in the form's order (files are attachments, not lines), a value the
 *  page filled in marked " (unverified)", the plugin's " (verified)" */
export function formMessageText(fields: FormFields, values: Record<string, unknown>, o: { marks: Record<string, string>; openProjects: readonly FormProjectRef[] }): string {
  return formInputsOf(fields.items).flatMap((f) => {
    const v = values[f.key];
    if (f.type === "file" || v === undefined || v === null || v === "" || (typeof v !== "string" && typeof v !== "boolean")) return [];
    const mark = MARK_WORDS[o.marks[f.key] as FormPrefillSource] ?? "";
    return [`${f.label}: ${shownValue(f, v, o.openProjects)}${mark}`];
  }).join("\n");
}

/** the marks worth keeping: only for a value that was kept and is not the company's fixed hidden value; a mark for a key the form lacks is
 *  the caller's refusal (null), and so is a mark under a Text block's key (a block is never filled in) */
export function formMarksOf(fields: FormFields, kept: FormValues, marks: Record<string, FormPrefillSource>): Record<string, FormPrefillSource> | null {
  const byKey = new Map(formInputsOf(fields.items).map((f) => [f.key, f]));
  const out: Record<string, FormPrefillSource> = {};
  for (const [key, source] of Object.entries(marks)) {
    const f = byKey.get(key);
    if (!f) return null;
    if (kept[key] !== undefined && !isFixedHidden(f)) out[key] = source;
  }
  return out;
}

/** the value a mapped field holds after the check (the address, the name, the subject), or null */
export function formMappedValue(fields: FormFields, values: FormValues, mapping: FormField["mapping"]): string | null {
  const f = formInputsOf(fields.items).find((x) => x.mapping === mapping);
  const v = f ? values[f.key] : undefined;
  return typeof v === "string" && v ? v : null;
}
